import React, { forwardRef, Ref } from 'react';
import { action } from 'mobx';
import classnames from 'classnames';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import Text from '../../../../../../global/text/text';
import { CopyButton } from '../../../../../../global/copy-button/copy-button';
import { IConnectionDetailsDTO, onChangeConnectionEntity } from '../../../../../../../api/connections.dao';
import { makeAssignToParticipantFormInPopover } from '../assign-to-participant-modal/make-assign-to-participant-form-in-popover';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { MeetingConnectionsAggregateRoot } from '../../../../../../../modules/connections/meeting/meeting-connections.aggregate-root';
import { OrganizationRole } from '../../../../../../../modules/connections/types';
import { IAttendeeOrganizationDTO } from '../../../../../../../api/attendees.dao';
import { hasPermission } from '../../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../../api/roles.dao';
import { uniqueJoiningLink } from '../../../../shared/_connection-dropdown/connection-dropdown.presentation';
import { editText } from '../../../../../../../constants/common-labels.constants';

export interface IOpenFormInPopOverBtnProps {
    isOpen: boolean;
    isAssignButtonDisabled: boolean;
    meetingConnectionId: number;
    itemsList: IAttendeeOrganizationDTO[];
    onChange: ((attendeeEntityId: number, orgEntityId: number, meetingConnectionId: number, originRole: OrganizationRole) => void);
}

export const OpenFormInPopOverBtn = makeAssignToParticipantFormInPopover(
    forwardRef((props: IOpenFormInPopOverBtnProps, ref: Ref<HTMLInputElement>) => {
        return <DefaultButton
            disabled={props.isAssignButtonDisabled}
            text={<FormattedMessage id='oe.common.assignToAttendee' description='button label' defaultMessage='+ Assign to Attendee' />}
            ref={ref}
            modifier={['no-border']}
            className={classnames('default-button default-button__add-button', {
                'default-button__add-button-active': props.isOpen
            })}
        />;
    })
);

interface IProps {
    unassignedMeetingConnections: IConnectionDetailsDTO[];
    formData: MeetingConnectionsAggregateRoot;
    onChangeMeetingConnection: (entity: onChangeConnectionEntity, connectionId: number | null) => void;
    attendeesList: IAttendeeOrganizationDTO[];
    setAssigningConnection: React.Dispatch<React.SetStateAction<boolean>>;
    editConnection: (connection: IConnectionDetailsDTO) => void;
    knovioReplayConnection: IConnectionDetailsDTO;
}

const UnassignedLinkList = mobxReact.observer(
    (props: IProps): JSX.Element => {

        const {
            unassignedMeetingConnections,
            formData,
            attendeesList,
            setAssigningConnection,
            editConnection,
            knovioReplayConnection,
        } = props;

        const onChange = action((
            attendeeEntityId: number,
            orgEntityId: number,
            meetingConnectionId: number,
            originRole: OrganizationRole
        ): void => {
            const entity = formData.getOrganizationsByRole(originRole)
                ?.find(org => org?.getProps()?.organizationId === orgEntityId)
                ?.getEntities()
                ?.find(item => item.getProps().attendeeId === attendeeEntityId);

            entity.setPropsValue('meetingConnectionType', uniqueJoiningLink);
            props.onChangeMeetingConnection(entity, meetingConnectionId);
            setAssigningConnection(prev => !prev);
        });

        const unassignedLinksCount = unassignedMeetingConnections.length;
        const isAssignButtonDisabled = !!knovioReplayConnection;

        return (
            <div className='attendee-list'>
                <div className='attendee-list__row'>
                    <div className='attendee-list__label'>
                        {unassignedLinksCount} <FormattedMessage id={`oe.input.unassignedLinks`} description='Input label'
                            defaultMessage={'Unassigned Links'}
                        />
                    </div>
                </div>
                {unassignedMeetingConnections.map(entity => {
                    return (
                        <div
                            className='attendee-connection organization__row'
                            key={`${entity.connectionId}_${entity.meetingConnectionId}`}
                        >
                            <div
                                className='attendee-connection__wrapper'
                            >
                                <div>
                                    <Text className='organization__attendee--no-link' tag='div' message={entity.attendeeEmail || 'No Email'} withEllipsis />
                                    <OpenFormInPopOverBtn
                                        itemsList={attendeesList}
                                        onChange={onChange}
                                        meetingConnectionId={entity.meetingConnectionId}
                                        isAssignButtonDisabled={isAssignButtonDisabled}
                                    />
                                </div>
                                {hasPermission(Permission.CONNECTIONS_MANAGEMENT) &&
                                    <DefaultButton
                                        onClick={() => editConnection(entity)}
                                        className='edit-while-hovered'
                                        modifier={['slim', 'link']}
                                        text={editText()}
                                    />
                                }
                            </div>
                            <CopyButton
                                copyValue={entity.connectionLink}
                                withoutIcon
                                withCopyLink
                            />
                        </div>
                    );
                })}
            </div>
        );
    });

export { UnassignedLinkList };
